import Card from '@pelckmans/elementis/components/card';
import Results from '@pelckmans/elementis/components/results';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import type { ExerciseNode } from '../../../../api/exercise-node';
import SublevelSummary from '../../../../components/module/SublevelSummary';
import { useMaterialLinkBuilder } from '../../../../hooks/useMaterialLinkBuilder';
import LoadingState from '../../../../shared-components/LoadingState';
import { buildGroupProps } from '../../../../utils/elementisUtils';
import { getResultPercentages } from '../../../../utils/results';
import { findDeepNodeById } from '../../../../utils/tocNode';
import { useExerciseModuleContext } from '../../_contexts/ExerciseModuleContext';
import TableOfContentsContext from '../_contexts/TableOfContentsContext';

function ExerciseNodeCard({ exerciseNode }: { exerciseNode: ExerciseNode }) {
  const { buildMaterialTocLink } = useMaterialLinkBuilder();

  return (
    <Card
      key={exerciseNode.id}
      as="link"
      href={buildMaterialTocLink(exerciseNode.id) || ''}
      titleTag="h3"
      titleText={exerciseNode.description}
      tags={buildGroupProps(
        <SublevelSummary
          sublevels={exerciseNode.nodes}
          totalExercises={exerciseNode.exerciseCount}
          completedExercises={exerciseNode.completedExerciseCount}
          contentTypeSummary={exerciseNode.contentTypeSummary}
        />,
      )}
      results={
        (exerciseNode.score?.maximum || 0) > 0 ? (
          <Results quantifiable {...getResultPercentages(exerciseNode.score)} />
        ) : undefined
      }
    />
  );
}

export default function ExerciseNodeCardSection() {
  const { exerciseNodes, isLoading } = useExerciseModuleContext();
  const { selectedNode } = useContext(TableOfContentsContext);
  const { t } = useTranslation();

  if (!selectedNode) return null;
  const currentExerciseNode = findDeepNodeById<ExerciseNode>(exerciseNodes, selectedNode.id);
  if (!currentExerciseNode || currentExerciseNode.nodes.length === 0) return null;

  return (
    <div className="exercise-module">
      <section className="list-cards">
        {isLoading ? (
          <LoadingState variant="list" aria-label={t('PAGES.VEM.CARDS.LOADING.A11Y_LABEL')} size={6} height={82} />
        ) : (
          currentExerciseNode.nodes.map(exerciseNode => (
            <ExerciseNodeCard key={exerciseNode.id} exerciseNode={exerciseNode} />
          ))
        )}
      </section>
    </div>
  );
}
